/**
 * T15405 · Platform slot · netInfo — web implementation.
 *
 * Reads `navigator.onLine` synchronously. Subscribes to the `online` and
 * `offline` window events to forward changes. Web doesn't expose
 * connection type via a stable API, so `type` is reported as
 * `'unknown'`.
 *
 * The `window` reference is injected for test determinism and SSR
 * tolerance.
 */

import type { NetInfoSlot, NetInfoSnapshot } from '../adapter.js';

export interface WindowLike {
  addEventListener(type: 'online' | 'offline', listener: () => void): void;
  removeEventListener(type: 'online' | 'offline', listener: () => void): void;
}

export interface NavigatorLike {
  readonly onLine: boolean;
}

export interface WebNetInfoDeps {
  readonly window?: WindowLike;
  readonly nav?: NavigatorLike;
}

export function createWebNetInfoSlot(deps: WebNetInfoDeps = {}): NetInfoSlot {
  const win = deps.window ?? (typeof window !== 'undefined' ? window : undefined);
  const nav = deps.nav ?? (typeof navigator !== 'undefined' ? navigator : undefined);

  return {
    current(): NetInfoSnapshot {
      return {
        online: nav?.onLine ?? true,
        type: 'unknown',
      };
    },
    addListener(cb): () => void {
      if (win === undefined) {
        return (): void => {
          /* SSR: no events to subscribe to */
        };
      }
      const on = (): void => cb(true);
      const off = (): void => cb(false);
      win.addEventListener('online', on);
      win.addEventListener('offline', off);
      let removed = false;
      return (): void => {
        if (removed) return; // idempotent
        removed = true;
        win.removeEventListener('online', on);
        win.removeEventListener('offline', off);
      };
    },
  };
}
